import React, { useEffect, useState } from 'react';
import { Upload, Download, RefreshCw, Trash2, Loader2, Database, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';

const STATUS_BADGE = {
  importable: { cls: 'bg-green-100 text-green-800', label: 'Ready' },
  needs_mapping: { cls: 'bg-amber-100 text-amber-800', label: 'Map product' },
  invalid: { cls: 'bg-red-100 text-red-700', label: 'Invalid' },
  duplicate: { cls: 'bg-gray-200 text-gray-700', label: 'Duplicate' },
};

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length);
  if (!lines.length) return [];
  const parseLine = (line) => {
    const out = [];
    let cur = '';
    let q = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (q) {
        if (ch === '"') {
          if (line[i + 1] === '"') { cur += '"'; i += 1; } else { q = false; }
        } else { cur += ch; }
      } else if (ch === '"') { q = true; }
      else if (ch === ',') { out.push(cur); cur = ''; }
      else { cur += ch; }
    }
    out.push(cur);
    return out;
  };
  const headers = parseLine(lines[0]).map((h) => h.trim());
  return lines.slice(1).map((ln) => {
    const cells = parseLine(ln);
    const o = {};
    headers.forEach((h, i) => { o[h] = (cells[i] ?? '').trim(); });
    return o;
  });
};

const OrderInsights = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    adminService.getProducts().then((p) => setProducts(Array.isArray(p) ? p : (p.products || []))).catch(() => {});
  }, []);

  // ---- Import tab ----
  const [rawRows, setRawRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [mappings, setMappings] = useState({});
  const [overwrite, setOverwrite] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const handleTemplate = async () => {
    try {
      const blob = await adminService.downloadSalesHistoryTemplate();
      const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sales_history_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);
    if (!rows.length) { toast.error('No rows found in the CSV'); return; }
    setRawRows(rows);
    setApplyResult(null);
    setPreviewing(true);
    try {
      const res = await adminService.previewSalesHistoryImport(rows);
      setPreview(res);
      setMappings({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await adminService.applySalesHistoryImport({
        rows: rawRows, product_mappings: mappings, overwrite_duplicates: overwrite,
      });
      setApplyResult(res);
      toast.success(`Imported — ${res.inserted} added, ${res.updated} updated, ${res.skipped} skipped`);
      // Refresh preview so duplicates reflect the new state.
      const refreshed = await adminService.previewSalesHistoryImport(rawRows);
      setPreview(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setApplying(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await adminService.syncSalesHistory();
      toast.success(`Synced ${res.rows_processed} rows from ${res.invoices_scanned} invoices`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const summary = preview?.summary;
  const needsMappingCount = summary?.needs_mapping || 0;
  const unmappedRemaining = (preview?.rows || []).filter((r) => r.status === 'needs_mapping' && !mappings[r.product_name]).length;

  // ---- Data tab ----
  const [filters, setFilters] = useState({ client_account_id: '', start: '', end: '' });
  const [dataRows, setDataRows] = useState([]);
  const [dataTotal, setDataTotal] = useState(0);
  const [loadingData, setLoadingData] = useState(false);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (filters.client_account_id) params.client_account_id = filters.client_account_id.trim();
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const res = await adminService.listSalesHistory(params);
      setDataRows(res.items || []);
      setDataTotal(res.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load sales history');
    } finally {
      setLoadingData(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm(`Delete all imported rows from batch ${batchId}? This cannot be undone.`)) return;
    try {
      const res = await adminService.deleteSalesHistoryBatch(batchId);
      toast.success(`Deleted ${res.deleted} row(s)`);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="order-insights-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Order Insights</h1>
        <p className="text-sm text-gray-600 mt-1">
          Sell-through data that powers order suggestions. Import pre-platform history or sync from finalized invoices.
        </p>
      </div>

      <Tabs defaultValue="import">
        <TabsList data-testid="order-insights-tabs">
          <TabsTrigger value="import" data-testid="tab-import"><FileSpreadsheet className="h-4 w-4 mr-1" /> Import</TabsTrigger>
          <TabsTrigger value="data" data-testid="tab-data" onClick={loadData}><Database className="h-4 w-4 mr-1" /> Data</TabsTrigger>
        </TabsList>

        {/* IMPORT */}
        <TabsContent value="import" className="space-y-4">
          <Card>
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={handleTemplate} data-testid="download-template-btn">
                <Download className="h-4 w-4 mr-2" /> Download CSV template
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".csv" className="hidden" onChange={handleFile} data-testid="csv-file-input" />
                <span className="inline-flex items-center px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" /> Upload CSV
                </span>
              </label>
              <div className="flex-1" />
              <Button variant="outline" onClick={handleSync} disabled={syncing} data-testid="sync-invoices-btn">
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Sync from invoices
              </Button>
            </CardContent>
          </Card>

          {previewing && (
            <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Validating…</div>
          )}

          {preview && (
            <>
              <div className="flex flex-wrap gap-2" data-testid="import-summary">
                <Badge className="bg-green-100 text-green-800">{summary.importable} ready</Badge>
                <Badge className="bg-amber-100 text-amber-800">{needsMappingCount} need mapping</Badge>
                <Badge className="bg-gray-200 text-gray-700">{summary.duplicate} duplicate</Badge>
                <Badge className="bg-red-100 text-red-700">{summary.invalid} invalid</Badge>
                <Badge variant="outline">{summary.total} total</Badge>
              </div>

              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-2 py-2">Date</th>
                      <th className="px-2 py-2">Ordered</th>
                      <th className="px-2 py-2">Sold</th>
                      <th className="px-2 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {preview.rows.map((r) => {
                      const b = STATUS_BADGE[r.status];
                      return (
                        <tr key={r.row} data-testid={`preview-row-${r.row}`}>
                          <td className="px-3 py-2 text-gray-400">{r.row + 1}</td>
                          <td className="px-3 py-2">{r.company_name || r.client_key}</td>
                          <td className="px-3 py-2">
                            {r.status === 'needs_mapping' ? (
                              <Select
                                value={mappings[r.product_name] || ''}
                                onValueChange={(v) => setMappings((m) => ({ ...m, [r.product_name]: v }))}
                              >
                                <SelectTrigger className="h-8 w-56" data-testid={`map-select-${r.row}`}>
                                  <SelectValue placeholder={`Map "${r.product_name}"`} />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              r.product_display_name || r.product_name
                            )}
                          </td>
                          <td className="px-2 py-2 text-center">{r.delivery_date || '—'}</td>
                          <td className="px-2 py-2 text-center">{r.qty_ordered ?? '—'}</td>
                          <td className="px-2 py-2 text-center">{r.qty_sold ?? '—'}</td>
                          <td className="px-2 py-2 text-center">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${b?.cls}`}>{b?.label}</span>
                            {r.message && r.status === 'invalid' && (
                              <p className="text-[10px] text-red-500 mt-0.5">{r.message}</p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="overwrite-check" />
                  Overwrite existing imported rows for the same client/product/date
                </label>
                <Button
                  className="bg-amber-500 hover:bg-amber-600"
                  onClick={handleApply}
                  disabled={applying}
                  data-testid="apply-import-btn"
                >
                  {applying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : 'Apply import'}
                </Button>
              </div>
              {unmappedRemaining > 0 && (
                <p className="text-xs text-amber-600">{unmappedRemaining} row(s) still need a product mapping and will be skipped unless mapped.</p>
              )}

              {applyResult && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-center gap-2" data-testid="apply-result">
                  <CheckCircle2 className="h-4 w-4" />
                  Imported: {applyResult.inserted} added, {applyResult.updated} updated, {applyResult.skipped} skipped, {applyResult.invalid} invalid. Batch: {applyResult.batch_id.slice(0, 8)}…
                </div>
              )}
            </>
          )}

          {!preview && !previewing && (
            <div className="text-center py-12 text-gray-400" data-testid="import-empty">
              <FileSpreadsheet className="h-10 w-10 mx-auto mb-3" />
              Download the template, fill it in, and upload to preview your import.
            </div>
          )}
        </TabsContent>

        {/* DATA */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs">Client account ID</Label>
                <Input value={filters.client_account_id} onChange={(e) => setFilters((f) => ({ ...f, client_account_id: e.target.value }))} placeholder="optional" data-testid="data-client-filter" />
              </div>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={filters.start} onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} data-testid="data-start-filter" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={filters.end} onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} data-testid="data-end-filter" />
              </div>
              <Button variant="outline" onClick={loadData} disabled={loadingData} data-testid="data-load-btn">
                {loadingData ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Load
              </Button>
            </CardContent>
          </Card>

          <p className="text-sm text-gray-500">{dataTotal} row(s)</p>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Client</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-2 py-2">Ordered</th>
                  <th className="px-2 py-2">Delivered</th>
                  <th className="px-2 py-2">Sold</th>
                  <th className="px-2 py-2">Source</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dataRows.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">No rows — adjust filters and Load.</td></tr>
                ) : dataRows.map((r) => (
                  <tr key={r.id} data-testid={`data-row-${r.id}`}>
                    <td className="px-3 py-2">{r.delivery_date}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{r.client_account_id}</td>
                    <td className="px-3 py-2">{r.product_name}</td>
                    <td className="px-2 py-2 text-center">{r.qty_ordered}</td>
                    <td className="px-2 py-2 text-center">{r.qty_delivered}</td>
                    <td className="px-2 py-2 text-center font-medium">{r.qty_sold}</td>
                    <td className="px-2 py-2 text-center">
                      <Badge className={r.source === 'invoice' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>{r.source}</Badge>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {r.source === 'imported' && (
                        <button
                          className="text-red-500 hover:text-red-700"
                          title={`Delete import batch ${r.source_ref}`}
                          onClick={() => handleDeleteBatch(r.source_ref)}
                          data-testid={`delete-batch-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrderInsights;
